/**
 * @fileoverview UI Store (Zustand)
 *
 * UI state shared by the whole admin: the colour theme, the sidebar, the bottom bar.
 *
 * Theme
 * -----
 * One preference per device: 'light' | 'dark' | 'system' (follow the device). It is kept
 * in localStorage under THEME_STORAGE_KEY (read/written in try/catch: private windows and
 * blocked storage just fall back to light). `isDarkMode` is the RESOLVED value — every
 * existing consumer (POS, order board, co-gérant, …) keeps reading it, and the same value
 * drives the `dark` class + `data-theme` on <html> (Tailwind `darkMode: 'class'`, the
 * global dark layer in src/app/dark.css) and the MUI theme (App.jsx). Nothing can go out
 * of sync because there is only this one source.
 *
 * index.html applies the class before React starts (no white flash); keep its logic in
 * step with resolveTheme() / isLightOnlyPath() below.
 *
 * @module stores/useUIStore
 */

import { create } from 'zustand';
import { persist, createJSONStorage } from 'zustand/middleware';

export const THEME_STORAGE_KEY = 'ods-theme';
export const THEME_PREFERENCES = ['light', 'dark', 'system'];

/** Screens that always stay light (sign-in flow, the customer-facing screen). */
export const isLightOnlyPath = (pathname = '') =>
  /^\/(admin\/)?(login|register|forget-password|reset_password|reset-password|auth\/google)(\/|$)/.test(pathname)
  || /^\/(claim|screen)(\/|$)/.test(pathname)
  || pathname === '/';

const safeGet = (key) => {
  try { return window.localStorage.getItem(key); } catch { return null; }
};
const safeSet = (key, value) => {
  try { window.localStorage.setItem(key, value); } catch { /* storage blocked: keep it in memory */ }
};

const systemPrefersDark = () => {
  try {
    return !!window.matchMedia && window.matchMedia('(prefers-color-scheme: dark)').matches;
  } catch {
    return false;
  }
};

/** A phone-sized screen (the default theme is dark there). */
export const isPhoneScreen = () => {
  try {
    return !!window.matchMedia && window.matchMedia('(max-width: 767px)').matches;
  } catch {
    return false;
  }
};

/** The preference saved on this device (when nothing valid is saved: 'dark' on a phone, else 'light'). */
export const readThemePreference = () => {
  if (typeof window === 'undefined') return 'light';
  const saved = safeGet(THEME_STORAGE_KEY);
  if (THEME_PREFERENCES.includes(saved)) return saved;
  // Before the theme choice existed, the POS toggle saved { isDarkMode } in 'ui-store'.
  try {
    const legacy = JSON.parse(safeGet('ui-store') || '{}');
    if (legacy?.state?.isDarkMode === true) return 'dark';
  } catch { /* ignore */ }
  // owner 15/10: on a phone, dark is the default until the person picks a theme
  return isPhoneScreen() ? 'dark' : 'light';
};

export const resolveTheme = (preference) =>
  preference === 'dark' || (preference === 'system' && systemPrefersDark());

/** Apply the resolved theme to the document (class, data-theme, color-scheme). */
export const applyThemeToDocument = (isDark, { pathname } = {}) => {
  if (typeof document === 'undefined') return;
  const path = pathname ?? (typeof window !== 'undefined' ? window.location.pathname : '');
  const dark = !!isDark && !isLightOnlyPath(path);
  const root = document.documentElement;
  root.classList.toggle('dark', dark);
  root.setAttribute('data-theme', dark ? 'dark' : 'light');
  root.setAttribute('data-dark-mode', String(dark));
  root.style.colorScheme = dark ? 'dark' : 'light';
  // the old inline colours set by the first dark mode: the CSS tokens own them now
  root.style.backgroundColor = '';
  if (document.body) {
    document.body.classList.toggle('dark-mode', dark);
    document.body.style.backgroundColor = '';
    document.body.style.color = '';
  }
  const meta = document.querySelector('meta[name="theme-color"]');
  if (meta) meta.setAttribute('content', dark ? '#0e1322' : '#1976d2');
};

const initialPreference = readThemePreference();

/**
 * UI Store - Theme, sidebar and bottom bar state
 *
 * @example
 * const isDarkMode = useUIStore((s) => s.isDarkMode);
 * const setThemePreference = useUIStore((s) => s.setThemePreference);
 */
export const useUIStore = create(
  persist(
    (set, get) => ({
      // Theme
      themePreference: initialPreference,
      isDarkMode: resolveTheme(initialPreference),

      setThemePreference: (preference) => {
        const pref = THEME_PREFERENCES.includes(preference) ? preference : 'light';
        safeSet(THEME_STORAGE_KEY, pref);
        const isDarkMode = resolveTheme(pref);
        set({ themePreference: pref, isDarkMode });
        applyThemeToDocument(isDarkMode);
      },

      /** The sun / moon button: an explicit light or dark choice. */
      toggleDarkMode: () => get().setThemePreference(get().isDarkMode ? 'light' : 'dark'),

      setIsDarkMode: (value) => get().setThemePreference(value ? 'dark' : 'light'),

      /** Re-read the device setting (used while the preference is 'system'). */
      syncSystemTheme: () => {
        if (get().themePreference !== 'system') return;
        const isDarkMode = resolveTheme('system');
        if (isDarkMode !== get().isDarkMode) set({ isDarkMode });
        applyThemeToDocument(isDarkMode);
      },

      // Sidebar State
      isSidebarCollapsed: false,
      toggleSidebar: () => set((state) => ({ isSidebarCollapsed: !state.isSidebarCollapsed })),
      setSidebarCollapsed: (value) => set({ isSidebarCollapsed: value }),

      // Hide Bottom Bar State (for modals like Payment Modal)
      hideBottomBar: false,
      setHideBottomBar: (value) => set({ hideBottomBar: value }),
    }),
    {
      name: 'ui-store', // localStorage key (sidebar only — the theme has its own key)
      storage: createJSONStorage(() => ({
        getItem: (k) => safeGet(k),
        setItem: (k, v) => safeSet(k, v),
        removeItem: (k) => { try { window.localStorage.removeItem(k); } catch { /* ignore */ } },
      })),
      partialize: (state) => ({
        isSidebarCollapsed: state.isSidebarCollapsed,
      }),
      // the theme is never taken from 'ui-store' (it would override the device choice)
      merge: (persisted, current) => ({
        ...current,
        isSidebarCollapsed: !!persisted?.isSidebarCollapsed,
      }),
    }
  )
);

// Follow the device while the preference is 'system'.
if (typeof window !== 'undefined') {
  try {
    const mq = window.matchMedia && window.matchMedia('(prefers-color-scheme: dark)');
    const onChange = () => useUIStore.getState().syncSystemTheme();
    if (mq?.addEventListener) mq.addEventListener('change', onChange);
    else if (mq?.addListener) mq.addListener(onChange);
  } catch { /* ignore */ }
  // another tab of this device changed the theme
  window.addEventListener('storage', (e) => {
    if (e.key === THEME_STORAGE_KEY && THEME_PREFERENCES.includes(e.newValue)) {
      useUIStore.getState().setThemePreference(e.newValue);
    }
  });
}

// Legacy hook (DarkModeContext replacement): import { useDarkMode } from '@/stores';
export const useDarkMode = () => {
  const isDarkMode = useUIStore((state) => state.isDarkMode);
  const toggleDarkMode = useUIStore((state) => state.toggleDarkMode);
  const setIsDarkMode = useUIStore((state) => state.setIsDarkMode);
  const themePreference = useUIStore((state) => state.themePreference);
  const setThemePreference = useUIStore((state) => state.setThemePreference);

  return { isDarkMode, toggleDarkMode, setIsDarkMode, themePreference, setThemePreference };
};
